import gql from 'graphql-tag';

/**
 * Query to fetch the currently authenticated user's complete profile information.
 *
 * @returns Full user profile including personal info, avatar, banner, bio, social media, permissions, and metadata
 * @usedIn Account settings, edit profile, and authentication flows
 */
export const MY_USER = gql`
  query myUser {
    myUser {
      userId
      email
      emailVerified
      username
      preferredUsername
      displayName
      firstName
      lastName
      role
      title {
        typeId
        typeName
      }
      avatar {
        path
        sourceUrl
        sourceSet
        metadata {
          bucket
          mimetype
          s3Url
          filename
        }
      }
      banner {
        path
        sourceUrl
        sourceSet
        metadata {
          bucket
          mimetype
          s3Url
          filename
        }
      }
      bio
      socialMedia {
        type
        link
      }
      disabled
      preferred
      createdAt
      updatedAt
      permissionGroup {
        groupName
      }
    }
  }
`;

/**
 * Query to fetch all staff users.
 *
 * @returns List of staff members with their name, username, role, social media, and avatar
 * @usedIn Staff page layout
 */
export const STAFF = gql`
  query staffUsers {
    staffUsers {
      firstName
      lastName
      preferredUsername
      displayName
      role
      socialMedia {
        link
        type
      }
      avatar {
        sourceUrl
        sourceSet
      }
    }
  }
`;

/**
 * Query to check if an email address is already in use.
 *
 * @param email - The email address to check (optional)
 * @returns Boolean indicating if the email is already used
 * @usedIn Account settings and edit profile forms for email validation
 */
export const EMAIL_EXISTS = gql`
  query userExists($email: String) {
    emailUsed: userExists(email: $email)
  }
`;

/**
 * Query to check if a username is already in use.
 *
 * @param username - The preferred username to check (optional)
 * @returns Boolean indicating if the username is already used
 * @usedIn Account settings and edit profile forms for username validation
 */
export const USERNAME_EXISTS = gql`
  query userExists($username: String) {
    usernameUsed: userExists(preferredUsername: $username)
  }
`;

/**
 * Query to fetch directory users with optional filtering (grouped by first letter).
 * Does not pass `staff` — backend treats omitted `staff` as the default public directory.
 *
 * @param creditTypes - Array of credit type IDs to filter by (optional)
 * @param searchInput - Search term to filter users (optional)
 * @returns Directory results grouped by character, user details with credits, and available credit types
 * @usedIn Directory page layout
 */
export const USER_DIRECTORY = gql`
  query userDirectory($creditTypes: [ID!], $searchInput: String) {
    userDirectory(creditTypes: $creditTypes, searchInput: $searchInput) {
      char
      results {
        preferredUsername
        displayName
        role
        socialMedia {
          type
          link
        }
        avatar {
          sourceUrl
          sourceSet
        }
        credits {
          creditType {
            typeName
          }
        }
      }
    }
    creditTypes {
      typeId
      typeName
      typeDescription
    }
  }
`;

/**
 * Directory listing only (no root `creditTypes` field). Used with a separate `CREDIT_TYPES` query
 * so a resolver failure on `userDirectory` does not wipe the whole operation’s `data`.
 */
export const USER_DIRECTORY_USERS = gql`
  query userDirectoryUsers($creditTypes: [ID!], $searchInput: String) {
    userDirectory(creditTypes: $creditTypes, searchInput: $searchInput) {
      char
      results {
        preferredUsername
        displayName
        role
        socialMedia {
          type
          link
        }
        avatar {
          sourceUrl
          sourceSet
        }
        credits {
          creditType {
            typeName
          }
        }
      }
    }
  }
`;

/**
 * Query to fetch a user's public profile by preferred username.
 *
 * @param preferredUsername - The user's preferred username (required)
 * @returns Public user profile including basic info, avatar, banner, bio, social media, role, and permissions
 * @usedIn User profile page and getServerSideProps
 */
export const USER_PROFILE = gql`
  query profile($preferredUsername: String!) {
    profile(preferredUsername: $preferredUsername) {
      userId
      preferredUsername
      displayName
      preferred
      role
      title {
        typeId
        typeName
      }
      socialMedia {
        type
        link
      }
      avatar {
        sourceUrl
        sourceSet
      }
      banner {
        sourceUrl
        sourceSet
      }
      bio
      permissionGroup {
        groupName
      }
    }
  }
`;

/**
 * Query to check if either a username or email address is already in use.
 *
 * @param email - The email address to check (optional)
 * @param username - The preferred username to check (optional)
 * @returns Object with boolean flags for both emailUsed and usernameUsed
 * @usedIn Account settings form for combined validation
 */
export const USERNAME_OR_EMAIL_EXISTS = gql`
  query userExists($email: String, $username: String) {
    emailUsed: userExists(email: $email)
    usernameUsed: userExists(preferredUsername: $username)
  }
`;

/**
 * Query to search for users by input string.
 *
 * @param input - Search term to find users (required)
 * @returns List of matching users with their username, email, social media, and avatar
 * @usedIn User search components and autocomplete dropdowns
 */
export const SEARCH_USERS = gql`
  query searchUsers($input: String!) {
    searchUsers(input: $input) {
      preferredUsername
      email
      socialMedia {
        type
        link
      }
      avatar {
        sourceUrl
        sourceSet
      }
    }
  }
`;

/**
 * Query to fetch all authors (users who have authored posts).
 *
 * @returns List of authors with their preferred username, display name, and avatar
 * @usedIn Author selection components and dropdowns
 */
export const AUTHORS = gql`
  query authors {
    authors {
      preferredUsername
      displayName
      avatar {
        sourceUrl
        sourceSet
      }
    }
  }
`;
